import { Icon } from '@components/ui';
import { useLayoutEffect, useRef, useState } from 'react';

import styles from './TableCellValue.module.scss';

interface TableCellValueProps {
  content: React.ReactNode;
  isExpanded?: boolean;
  onExpand: (referenceElement: HTMLElement, content: React.ReactNode) => void;
}

export const TableCellValue = ({ content, isExpanded = false, onExpand }: TableCellValueProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    if (isExpanded) {
      setIsTruncated(false);
      return undefined;
    }

    const el = contentRef.current;
    if (!el) return undefined;

    const checkTruncation = () => setIsTruncated(el.scrollHeight > el.clientHeight + 1);
    checkTruncation();

    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el);
    return () => observer.disconnect();
  }, [content, isExpanded]);

  return (
    <div className={styles.cellValueWrapper}>
      <div
        ref={contentRef}
        className={isExpanded ? styles.cellValueExpanded : styles.cellValue}
      >
        {content}
      </div>
      {isTruncated && (
        <Icon
          name="expand"
          className={styles.expandCellButton}
          aria-label="Show full value"
          onClick={event => {
            event.stopPropagation();
            onExpand(contentRef.current ?? event.currentTarget, content);
          }}
        />
      )}
    </div>
  );
};
